// components/ContractCelebration.jsx
import { useState, useEffect, useRef } from 'react';
import { supabase } from '../utils/supabaseClient';
import { fmtMoney } from '../utils/constants';

// Confetti particle component
function Confetti() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const colors = ['#C4992A', '#1a2b4a', '#34D399', '#60A5FA', '#F97316', '#A78BFA'];
    const particles = Array.from({ length: 120 }, () => ({
      x: Math.random() * canvas.width,
      y: -20,
      w: Math.random() * 10 + 4,
      h: Math.random() * 6 + 3,
      color: colors[Math.floor(Math.random() * colors.length)],
      speed: Math.random() * 3 + 2,
      angle: Math.random() * Math.PI * 2,
      spin: (Math.random() - 0.5) * 0.2,
      drift: (Math.random() - 0.5) * 2,
    }));

    let frame;
    function draw() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach(p => {
        p.y += p.speed;
        p.x += p.drift;
        p.angle += p.spin;
        if (p.y > canvas.height) { p.y = -20; p.x = Math.random() * canvas.width; }
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.angle);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        ctx.restore();
      });
      frame = requestAnimationFrame(draw);
    }
    draw();

    // Stop confetti after 4 seconds
    const stop = setTimeout(() => cancelAnimationFrame(frame), 4000);
    return () => { cancelAnimationFrame(frame); clearTimeout(stop); };
  }, []);

  return (
    <canvas ref={canvasRef} style={{
      position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 1999,
    }} />
  );
}

export default function ContractCelebration({
  contract, s215, profile, apiKey, callAI, onClose,
}) {
  const [phase, setPhase] = useState('celebrate'); // celebrate | feedback
  const [pennyMessage, setPennyMessage] = useState('');
  const [loadingMessage, setLoadingMessage] = useState(true);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [wentWell, setWentWell] = useState('');
  const [couldImprove, setCouldImprove] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => { generateMessage(); }, []); // eslint-disable-line

  async function generateMessage() {
    setLoadingMessage(true);
    try {
      // Load analyst's celebration history to avoid repetition
      const { data: history } = await supabase
        .from('app_feedback')
        .select('celebration_type')
        .eq('analyst_name', profile?.contactName || '')
        .order('created_at', { ascending: false })
        .limit(10);

      const usedTypes = (history || []).map(h => h.celebration_type).filter(Boolean);

      const sys = 'You are Penny, the GoldenScope contracting assistant. A contract specialist just successfully closed out a contract. Write a warm, genuine, personalized congratulations message for them. ' +
        'Be specific to the contract details provided. Keep it to 2-3 sentences. Be encouraging and professional but warm — not robotic. ' +
        'End with one practical piece of wisdom or encouragement for their next contract. ' +
        (usedTypes.length > 0 ? 'IMPORTANT: You have already used these message styles for this analyst: ' + usedTypes.join(', ') + '. Use a completely different tone, angle, or focus this time.' : '') +
        '\n\nAlso include a JSON tag at the very end of your response (after the message) in this exact format: [TYPE:celebration_style] where celebration_style is a 2-3 word description of your message style (e.g., "milestone_focus" or "team_recognition" or "impact_highlight"). This tag is for internal tracking only.';

      const ctx = [
        'Analyst: ' + (profile?.contactName || 'Contract Specialist'),
        'Contract: ' + (contract.title || 'Contract'),
        'Contractor: ' + (s215?.contractorName || 'the contractor'),
        'Final Amount: ' + fmtMoney(s215?.maxAmount || contract.budget),
        'Procurement Method: ' + (contract.procMethod || 'competitive'),
        'Completed: ' + new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
      ].join('\n');

      const raw = await callAI(apiKey, {
        system: sys,
        model: 'claude-sonnet-4-6',
        max_tokens: 300,
        messages: [{ role: 'user', content: 'Write a congratulations message for this completed contract:\n\n' + ctx }],
      });

      // Extract celebration type tag and clean message
      const typeMatch = raw.match(/\[TYPE:([^\]]+)\]/);
      const celebType = typeMatch ? typeMatch[1].trim() : 'general_congrats';
      const cleanMessage = raw.replace(/\[TYPE:[^\]]+\]/, '').trim();

      setPennyMessage(cleanMessage);

      // Store celebration type for future reference (will be saved with feedback)
      window._celebType = celebType;
    } catch (e) {
      setPennyMessage('Congratulations on closing out ' + (contract.title || 'this contract') + '! Every completed contract is a win for Energy Safety and the people of California. Well done — on to the next one!');
    } finally {
      setLoadingMessage(false);
    }
  }

  async function submitFeedback() {
    setSubmitting(true);
    try {
      await supabase.from('app_feedback').insert({
        analyst_name: profile?.contactName || 'Unknown',
        analyst_email: profile?.contactEmail || null,
        contract_id: contract.id,
        contract_title: contract.title,
        rating,
        went_well: wentWell || null,
        could_improve: couldImprove || null,
        celebration_type: window._celebType || 'general_congrats',
        created_at: new Date().toISOString(),
      });
      setSubmitted(true);
      setTimeout(onClose, 2000);
    } catch (e) {
      console.error('Feedback submit error:', e);
      onClose();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <Confetti />
      <div style={{
        position: 'fixed', inset: 0, background: 'rgba(10,20,40,0.88)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 2000, padding: 24,
      }}>
        <div style={{
          background: 'var(--white)', borderRadius: 'var(--r-xl, 16px)',
          width: '100%', maxWidth: 520, overflow: 'hidden',
          boxShadow: '0 32px 80px rgba(0,0,0,0.4)',
        }}>

          {/* ── CELEBRATE PHASE ── */}
          {phase === 'celebrate' && (
            <div style={{ padding: '36px 36px 28px' }}>
              <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <div style={{ fontSize: 52, marginBottom: 12 }}>🎉</div>
                <h2 style={{ fontSize: 22, fontWeight: 800, color: 'var(--navy-900)', marginBottom: 6 }}>
                  Contract Closed!
                </h2>
                <div style={{ fontSize: 13.5, color: 'var(--slate-500)' }}>
                  {contract.title}{s215?.contractorName ? ' · ' + s215.contractorName : ''}
                </div>
                {(s215?.maxAmount || contract.budget) && (
                  <div style={{ fontSize: 13, color: 'var(--gold-600)', fontWeight: 600, marginTop: 4 }}>
                    {fmtMoney(s215?.maxAmount || contract.budget)}
                  </div>
                )}
              </div>

              {/* Penny message */}
              <div style={{
                background: 'var(--navy-900)', borderRadius: 'var(--r-lg)',
                padding: '20px 24px', marginBottom: 24,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                  <div style={{ width: 28, height: 28, borderRadius: 6, background: 'var(--gold-500)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>✒</div>
                  <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--gold-400)' }}>Penny</span>
                </div>
                {loadingMessage ? (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <div className="spin spin-sm" style={{ borderColor: 'rgba(255,255,255,.2)', borderTopColor: 'var(--gold-400)' }} />
                    <span style={{ fontSize: 13, color: 'var(--navy-300)' }}>Penny is writing you a note…</span>
                  </div>
                ) : (
                  <p style={{ fontSize: 14, color: 'var(--navy-100)', lineHeight: 1.8, margin: 0 }}>
                    {pennyMessage}
                  </p>
                )}
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  className="btn btn-gold"
                  style={{ flex: 1, justifyContent: 'center' }}
                  disabled={loadingMessage}
                  onClick={() => setPhase('feedback')}
                >
                  Share Feedback →
                </button>
                <button className="btn btn-ghost" style={{ flex: 1, justifyContent: 'center' }} onClick={onClose}>
                  Done
                </button>
              </div>
            </div>
          )}

          {/* ── FEEDBACK PHASE ── */}
          {phase === 'feedback' && (
            <div style={{ padding: '32px 36px 28px' }}>
              {submitted ? (
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                  <div style={{ fontSize: 40, marginBottom: 12 }}>🙏</div>
                  <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--navy-900)', marginBottom: 8 }}>Thank you!</div>
                  <div style={{ fontSize: 13.5, color: 'var(--slate-500)' }}>Your feedback helps make GoldenScope better for everyone at Energy Safety.</div>
                </div>
              ) : (
                <>
                  <h3 style={{ fontSize: 18, fontWeight: 800, color: 'var(--navy-900)', marginBottom: 6 }}>
                    Quick Feedback
                  </h3>
                  <p style={{ fontSize: 13, color: 'var(--slate-500)', marginBottom: 20, lineHeight: 1.6 }}>
                    How was your experience using GoldenScope on this contract? This goes directly to the developer — your input shapes what gets built next.
                  </p>

                  {/* Star rating */}
                  <div style={{ marginBottom: 18 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--slate-500)', marginBottom: 8 }}>Overall Rating</div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {[1, 2, 3, 4, 5].map(s => (
                        <button
                          key={s}
                          onClick={() => setRating(s)}
                          onMouseEnter={() => setHoverRating(s)}
                          onMouseLeave={() => setHoverRating(0)}
                          style={{
                            fontSize: 28, background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px',
                            color: s <= (hoverRating || rating) ? '#F59E0B' : '#D1D5DB',
                            transition: 'color 0.1s',
                          }}
                        >★</button>
                      ))}
                      {rating > 0 && (
                        <span style={{ fontSize: 12, color: 'var(--slate-400)', alignSelf: 'center', marginLeft: 4 }}>
                          {['', 'Needs work', 'Could be better', 'Pretty good', 'Really good', 'Excellent!'][rating]}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* What went well */}
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--slate-500)', display: 'block', marginBottom: 6 }}>
                      What worked well? <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</span>
                    </label>
                    <textarea
                      value={wentWell}
                      onChange={e => setWentWell(e.target.value)}
                      rows={2}
                      placeholder="e.g., Penny drafted the SOW in minutes, the DGS routing flags were super helpful…"
                      style={{ width: '100%', padding: '10px 12px', borderRadius: 'var(--r-md)', border: '1.5px solid var(--slate-200)', fontSize: 13, resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' }}
                    />
                  </div>

                  {/* What could improve */}
                  <div style={{ marginBottom: 20 }}>
                    <label style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--slate-500)', display: 'block', marginBottom: 6 }}>
                      Anything missing or that could be better? <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</span>
                    </label>
                    <textarea
                      value={couldImprove}
                      onChange={e => setCouldImprove(e.target.value)}
                      rows={2}
                      placeholder="e.g., It would help to have a way to track vendor insurance expiration dates…"
                      style={{ width: '100%', padding: '10px 12px', borderRadius: 'var(--r-md)', border: '1.5px solid var(--slate-200)', fontSize: 13, resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' }}
                    />
                  </div>

                  <div style={{ display: 'flex', gap: 10 }}>
                    <button
                      className="btn btn-gold"
                      style={{ flex: 1, justifyContent: 'center' }}
                      disabled={submitting || rating === 0}
                      onClick={submitFeedback}
                    >
                      {submitting ? 'Submitting…' : 'Submit Feedback'}
                    </button>
                    <button className="btn btn-ghost" style={{ flex: 1, justifyContent: 'center' }} onClick={onClose}>
                      Skip
                    </button>
                  </div>
                  {rating === 0 && (
                    <div style={{ fontSize: 11.5, color: 'var(--slate-400)', textAlign: 'center', marginTop: 8 }}>
                      Select a star rating to submit
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
